import {
  useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult,
} from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import {
  addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, updateDoc,
} from 'firebase/firestore';

import { db } from '../../config';
import { InMailTemplateInput, InMailTemplateRecord } from './types';

export const IN_MAIL_TEMPLATES_QUERY_KEY = ['in-mail-templates'] as const;

const COLLECTION = 'inMailTemplates';

/** Znacznik czasu z Firestore albo `null`, gdy zapis jeszcze nie wrócił z serwera. */
const toMillis = (value: unknown): number | null => {
  const stamp = value as { toMillis?: () => number } | undefined;
  return typeof stamp?.toMillis === 'function' ? stamp.toMillis() : null;
};

/**
 * Szablony wiadomości leżą w osobnej kolekcji, nie przy wysyłkach.
 *
 * Wysyłka to zdarzenie z konkretną treścią i listą odbiorców, a szablon żyje dalej i
 * bywa używany wielokrotnie. Trzymane razem, każda zmiana szablonu przepisywałaby historię
 * wysłanych wiadomości.
 */
export const useGetInMailTemplates = (): UseQueryResult<InMailTemplateRecord[], FirebaseError> => useQuery<
  InMailTemplateRecord[],
  FirebaseError
>({
  queryKey: IN_MAIL_TEMPLATES_QUERY_KEY,
  queryFn: async () => {
    const snapshot = await getDocs(query(collection(db, COLLECTION), orderBy('updatedAt', 'desc')));
    return snapshot.docs.map((entry) => {
      const data = entry.data();
      return {
        id: entry.id,
        name: typeof data.name === 'string' ? data.name : 'Bez nazwy',
        subject: typeof data.subject === 'string' ? data.subject : '',
        doc: data.doc,
        createdAt: toMillis(data.createdAt),
        updatedAt: toMillis(data.updatedAt),
      };
    });
  },
  refetchOnWindowFocus: false,
});

interface SaveParams {
  /** Puste id zakłada nowy szablon, wypełnione nadpisuje istniejący. */
  id?: string | null;
  data: InMailTemplateInput;
}

export const useSaveInMailTemplate = (): UseMutationResult<string, FirebaseError, SaveParams> => {
  const queryClient = useQueryClient();

  return useMutation<string, FirebaseError, SaveParams>({
    mutationFn: async ({ id, data }) => {
      if (id) {
        await updateDoc(doc(db, COLLECTION, id), { ...data, updatedAt: serverTimestamp() });
        return id;
      }
      const created = await addDoc(collection(db, COLLECTION), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return created.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IN_MAIL_TEMPLATES_QUERY_KEY });
    },
  });
};

export const useDeleteInMailTemplate = (): UseMutationResult<void, FirebaseError, { id: string }> => {
  const queryClient = useQueryClient();

  return useMutation<void, FirebaseError, { id: string }>({
    mutationFn: async ({ id }) => {
      await deleteDoc(doc(db, COLLECTION, id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IN_MAIL_TEMPLATES_QUERY_KEY });
    },
  });
};
